import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  TextInput,
  SafeAreaView,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { IconButton } from 'react-native-paper';
import LoadingButton from '../components/LoadingButton';
import ForgotPasswordModal from '../components/ForgotPasswordModal';

const { width, height } = Dimensions.get('window');

const SignInFieldsOnly = (props: {
  onSubmit: (email: string, password: string, logintype: string) => void;
  autoFocus?: boolean;
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({
    email: '',
    password: '',
  });
  const [isPasswordVisible, setPasswordVisible] = useState<boolean>(false);
  const [forgotModalVisible, setForgotModalVisible] = useState(false);
  
  // Ref for the email input to auto-focus
  const emailInputRef = useRef<TextInput>(null);

  // Auto-focus email input when component mounts if autoFocus is true
  useEffect(() => {
    if (props.autoFocus && emailInputRef.current) {
      // Delay to ensure component is rendered before focusing
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 500);
    }
  }, [props.autoFocus]);

  const validateEmail = useCallback((email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  const handleInputChange = useCallback(
    (field: string, value: string) => {
      if (field === 'email') {
        setEmail(value);
        // Only validate email if it's not empty or if user has started typing
        if (!value) {
          setErrors((prev) => ({
            ...prev,
            email: 'Please enter your email address.',
          }));
        } else if (!validateEmail(value)) {
          setErrors((prev) => ({
            ...prev,
            email: 'Please enter a valid email address.',
          }));
        } else {
          setErrors((prev) => ({ ...prev, email: '' }));
        }
      } else if (field === 'password') {
        setPassword(value);
        if (!value) {
          setErrors((prev) => ({
            ...prev,
            password: 'Please enter a password.',
          }));
        } else {
          setErrors((prev) => ({ ...prev, password: '' }));
        }
      }
    },
    [validateEmail]
  );

  const handleForgotPasswordSuccess = useCallback((message: string) => {
    console.log('Password reset email sent:', message);
  }, []);

  const handleForgotPasswordError = useCallback((message: string) => {
    console.log('Password reset error:', message);
  }, []);

  const handleSubmit = useCallback(async () => {
    props.onSubmit(email, password, 'meander');
  }, [email, password, props]);

  const togglePasswordVisibility = useCallback(() => {
    setPasswordVisible((prev) => !prev);
  }, []);

  const openForgotModal = useCallback(() => {
    setForgotModalVisible(true);
  }, []);

  const closeForgotModal = useCallback(() => {
    setForgotModalVisible(false);
  }, []);

  // Memoize styles to prevent recreation on every render
  const containerStyle = useMemo(
    () => [styles.containerBgColor, { paddingBottom: 5 }],
    []
  );
  const emailInputStyle = useMemo(
    () => [styles.BgColor, { borderRadius: 25 }],
    []
  );
  const passwordInputStyle = useMemo(
    () => [styles.BgColor, { borderRadius: 25 }],
    []
  );
  const forgotPasswordStyle = useMemo(
    () => ({
      color: '#39BEB7',
      fontSize: 14,
      textDecorationLine: 'underline' as const,
    }),
    []
  );

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={{ flex: 1 }}
    >
      <SafeAreaView>
        <View style={containerStyle}>
          <View>
            <Text style={styles.labelText}>Email</Text>
            <TextInput
              ref={emailInputRef}
              placeholder="Enter your email"
              placeholderTextColor={'#8D8E90'}
              style={emailInputStyle}
              className="p-4 w-full text-white"
              value={email}
              onChangeText={(value: string) =>
                handleInputChange('email', value)
              }
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              returnKeyType="next"
            />
            <View style={styles.errorContainer}>
              {errors.email ? (
                <Text style={styles.errorText}>{errors.email}</Text>
              ) : null}
            </View>

            <Text style={styles.labelText}>Password</Text>
            <View style={styles.passwordContainer}>
              <TextInput
                placeholder="Enter your password"
                placeholderTextColor={'#8D8E90'}
                style={[passwordInputStyle, styles.passwordInput]}
                className="p-4 w-full text-white"
                secureTextEntry={!isPasswordVisible}
                value={password}
                onChangeText={(value: string) =>
                  handleInputChange('password', value)
                }
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="done"
              />
              <IconButton
                icon={(props) => (
                  <MaterialCommunityIcons
                    name={isPasswordVisible ? 'eye' : 'eye-off'}
                    {...props}
                    style={{ color: '#01556F' }}
                  />
                )}
                onPress={togglePasswordVisibility}
                style={styles.alignIcon}
              />
            </View>
            <View style={styles.errorContainer}>
              {errors.password ? (
                <Text style={styles.errorText}>{errors.password}</Text>
              ) : null}
            </View>

            <Pressable
              onPress={openForgotModal}
              style={styles.forgotPasswordContainer}
            >
              <Text style={forgotPasswordStyle}>Forgot your Password?</Text>
            </Pressable>

            <View style={styles.buttonContainer}>
              <LoadingButton
                onPress={handleSubmit}
                className="p-1 mt-5 text-center rounded-full"
              >
                Log in
              </LoadingButton>
            </View>
          </View>
        </View>

        {/* Forgot Password Modal */}
        <ForgotPasswordModal
          visible={forgotModalVisible}
          onClose={closeForgotModal}
          onSuccess={handleForgotPasswordSuccess}
          onError={handleForgotPasswordError}
        />
      </SafeAreaView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  containerBgColor: {
    backgroundColor: '#1A1D21',
  },
  labelText: {
    paddingTop: 12,
    paddingBottom: 8,
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  BgColor: {
    backgroundColor: '#292D33',
  },
  errorText: {
    color: '#f38787',
    fontSize: 14,
    marginTop: 4,
  },
  button: {
    backgroundColor: '#39BEB7',
  },
  passwordContainer: {
    position: 'relative',
    justifyContent: 'center',
  },
  passwordInput: {
    height: 48,
    paddingRight: 48, // space for the icon
  },
  alignIcon: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: [{ translateY: -24 }], // half of input height
    zIndex: 2,
  },
  forgotPasswordContainer: {
    marginTop: 8,
    alignSelf: 'flex-end',
  },
  buttonContainer: {
    marginTop: 15,
  },
  errorContainer: {
    minHeight: 18,
    justifyContent: 'flex-start',
  },
});

export default SignInFieldsOnly;
